import React, { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  CollectionPreferences,
  CollectionPreferencesProps,
  Container,
  Header,
  Pagination,
  SpaceBetween,
  Table,
  TextContent,
  TextFilter,
} from '@amzn/awsui-components-react-v3';
import {
  CONSUMER,
  CREATED,
  DEFER,
  GLADSTONE_GROUP,
  GLADSTONE_WORKSPACE,
  KEEP,
  PENDING_CONSUMER_REVIEW,
  RELINQUISH,
  TABLE_CONTENT_TYPE,
} from 'src/commons/constants';
import { listReviews, listSeasons } from 'src/api/notifications';
import { ListReviewsRequest, ListReviewsResponse, ReviewReviewer, ReviewReviewerList } from 'aws-sdk/clients/awsdlomni';
import { ResourceDetailsModal } from 'src/components/reviews/resourceDetailsModal';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { EmptyState } from 'src/commons/EmptyState';
import { createReviewDetailsLink, createReviewHistoryLink } from 'src/routes';
import { Link } from 'src/components/common/Link';
import { isValidReviewId } from 'src/components/reviews/utils';
import { ReviewStatus } from 'src/components/reviews/reviewStatus';
import { defaultWrapLinesPreference, largePageSizePreference } from 'src/commons/tables';
import { CommonPageProps } from 'src/components/common/CommonPageProps';
import { ReviewerUpdateModal } from 'src/components/reviews/reviewerUpdateModal';

export interface ReviewAssignmentProps extends CommonPageProps {
  userInfo: any;
  resourceItems: any;
  setResourceItems: any;
  decidedResources: any;
  setDecidedResources: any;
  readonly?: boolean;
}

function ReviewsAssignment(props: ReviewAssignmentProps) {
  const [resourceCount, setResourceCount] = useState(0);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [resourceModalVisible, setResourceModalVisible] = useState(false);
  const [selectedSchema, setSelectedSchema] = useState('{}');
  const [selectedResource, setSelectedResource] = useState<object>({});
  const [selectedReviewId, setSelectedReviewId] = useState<string>();
  const [expandedItems, setExpandedItems] = useState<ReviewReviewerList>();
  const [selectedResources, setSelectedResources] = useState([]);
  const [reviewerUpdateModalVisible, setReviewerUpdateModalVisible] = useState(false);
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 25,
  });

  useEffect(() => {
    props.setContentType(TABLE_CONTENT_TYPE);
    handleRefresh();
  }, []);

  useEffect(() => {
    deferIfReadOnly();
  }, [props.readonly, props.resourceItems]);

  const deferIfReadOnly = () => {
    if (props.readonly && props.resourceItems?.length) {
      const itemsToDefer = props.resourceItems.map((item) => ({
        ...item,
        action: DEFER,
      }));

      props.setDecidedResources((prev) => [...prev, ...itemsToDefer]);

      props.setResourceItems([]);
      setSelectedResources([]);
    }
  };

  const listAllReviews = async (request: ListReviewsRequest) => {
    let reviewers: ReviewReviewerList = [];
    let response: ListReviewsResponse;
    try {
      do {
        response = await listReviews(request);
        if (response.reviews?.length) {
          reviewers.push(...response.reviews);
        }
        request.nextToken = response.nextToken;
      } while (response.nextToken);
    } catch (e) {
      console.log(e);
    }
    return reviewers;
  };

  const handleRefresh = async () => {
    try {
      if (props.readonly) {
        return;
      }
      setLoadingReviews(true);
      props.setDecidedResources([]);
      let seasonsResponse = await listSeasons({
        status: 'RUNNING',
      });
      let reviewPromises = [];
      let reviews: ReviewReviewerList = [];

      seasonsResponse.seasons?.forEach((season) => {
        reviewPromises.push(
          listAllReviews({
            seasonId: season?.seasonId,
            status: 'ELIGIBLE',
          }),
        );

        props.userInfo?.memberGroupIds?.forEach((groupId) => {
          reviewPromises.push(
            listAllReviews({
              seasonId: season?.seasonId,
              status: 'ELIGIBLE',
              reviewerType: GLADSTONE_GROUP,
              reviewerId: groupId,
            }),
          );
        });

        props.userInfo?.memberWorkspaceIds?.forEach((workspaceId) => {
          reviewPromises.push(
            listAllReviews({
              seasonId: season?.seasonId,
              status: 'ELIGIBLE',
              reviewerType: GLADSTONE_WORKSPACE,
              reviewerId: workspaceId,
            }),
          );
        });
      });
      let results: ReviewReviewerList[] = await Promise.all(reviewPromises);
      const uniqueReviewIds = new Set();
      results.forEach((reviewList) => {
        reviewList
          .filter(
            (review) =>
              review.reviewerPersona == CONSUMER &&
              (review.reviewStatus == CREATED || review.reviewStatus == PENDING_CONSUMER_REVIEW),
          )
          .forEach((review) => {
            if (!uniqueReviewIds.has(review.reviewId)) {
              uniqueReviewIds.add(review.reviewId);
              reviews.push(review);
            }
          });
      });

      props.setResourceItems(reviews);
      setResourceCount(reviews.length);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingReviews(false);
    }
  };

  const getReviewInfo = (review: ReviewReviewer, infoName: string) => {
    switch (infoName) {
      case 'grant':
        return (
          <Link
            onClick={() => {
              setResourceModalVisible(true);
              setSelectedSchema(review.resourceTypeSchema);
              setSelectedResource(JSON.parse(review.resourceData ?? '{}'));
              setSelectedReviewId(review.reviewId);
            }}
          >
            View details
          </Link>
        );
      case 'type':
        return isValidReviewId(review.reviewId) ? (
          <Link external={true} href={createReviewDetailsLink(review.reviewId)}>
            {review.resourceType}
          </Link>
        ) : (
          review.reviewId
        );
      case 'description':
        return review.description ?? '-';
      case 'resource':
        if ((!review.resourceId && !review.name) || review.resourceId == '-') {
          return '-';
        }
        return (
          <Link external={true} href={review.link}>
            {review.name || review.resourceId}
          </Link>
        );
      case 'status':
        return <ReviewStatus status={review.reviewStatus} />;
    }
  };

  // Handlers for resources table
  const handleSelectionChange = ({ detail }) => {
    setSelectedResources(detail.selectedItems);
  };

  const selectAction = (action) => {
    const currentSelected = selectedResources;
    // append to decided resources the current selected items with the action property of the selected resources set to "Archive"
    props.setDecidedResources((prev) => [
      ...prev,
      ...currentSelected.map((item) => ({
        ...item,
        action: action,
      })),
    ]);
    // remove selected resources from the filtered resources
    props.setResourceItems(props.resourceItems.filter((item) => !currentSelected.includes(item)));
    setSelectedResources([]);
  };

  // Handlers for bulk actions
  const handleDefer = () => {
    if (selectedResources.length === 0) {
      return;
    }
    selectAction(DEFER);
  };

  const handleDelegate = () => {
    if (selectedResources.length === 0) {
      return;
    }
    setReviewerUpdateModalVisible(true);
  };

  const handleKeep = () => {
    if (selectedResources.length === 0) {
      return;
    }
    selectAction(KEEP);
  };

  const handleRelinquish = () => {
    if (selectedResources.length === 0) {
      return;
    }
    selectAction(RELINQUISH);
  };

  const handleDeferModal = () => {
    selectActionModal(DEFER);
  };

  const handleKeepModal = () => {
    selectActionModal(KEEP);
  };

  const handleRelinquishModal = () => {
    selectActionModal(RELINQUISH);
  };

  const selectActionModal = (action) => {
    props.setDecidedResources((prev) => [
      ...prev,
      ...props.resourceItems
        .filter((r) => r.reviewId === selectedReviewId)
        .map((item) => ({
          ...item,
          action: action,
        })),
    ]);
    // remove selected resource from the main table
    props.setResourceItems((prev) => prev.filter((item) => item.reviewId != selectedReviewId));
    setSelectedResources([]);
    setResourceModalVisible(false);
  };

  const { items, collectionProps, paginationProps, filterProps, filteredItemsCount } = useCollection(
    props.resourceItems,
    {
      filtering: {
        noMatch: {},
        empty: <EmptyState title='No matches' subtitle='No resources found.' />,
      },
      pagination: { pageSize: preferences.pageSize },

      sorting: {},
      selection: {},
    },
  );

  const getItemsGroupedByActionAndType = (items) => {
    const grouped = [];
    const actions = [...new Set(items.map((item) => item.action))];
    const resourceTypes = [...new Set(items.map((item) => item.resourceType))];

    actions.forEach((action) => {
      resourceTypes.forEach((resourceType) => {
        const children = items.filter((item) => item.action === action && item.resourceType === resourceType);

        if (children.length > 0) {
          grouped.push({
            resourceType,
            description: `${children.length} resources`,
            resourceId: '-',
            action,
            children,
            reviewId: `${resourceType}: ${action}`,
          });
        }
      });
    });

    return grouped;
  };

  const shouldShowResourcesTable = () => {
    return !props.readonly && (props.decidedResources?.length != resourceCount || resourceCount == 0);
  };

  return (
    <>
      <ReviewerUpdateModal
        {...props}
        visible={reviewerUpdateModalVisible}
        dismiss={() => {
          setReviewerUpdateModalVisible(false);
          selectAction(DEFER);
        }}
        reviewIds={selectedResources.map((resource) => resource.reviewId)}
        refresh={() => {}}
      />
      <ResourceDetailsModal
        onDismiss={() => {
          setResourceModalVisible(false);
        }}
        visible={resourceModalVisible}
        schema={selectedSchema}
        data={selectedResource}
        customButtons={
          <SpaceBetween direction='horizontal' size='xs'>
            <Button variant='normal' onClick={handleDeferModal}>
              Defer
            </Button>
            <Button variant='normal' onClick={handleRelinquishModal}>
              Relinquish access
            </Button>
            <Button variant='primary' onClick={handleKeepModal}>
              Keep access
            </Button>
          </SpaceBetween>
        }
      />
      <SpaceBetween size='m'>
        <SpaceBetween size='l'>
          <>
            {shouldShowResourcesTable() && (
              <>
                <Container
                  header={
                    <Header
                      description='Follow these steps to review your assigned resources'
                      variant='h2'
                      actions={
                        <Button
                          iconName='view-full'
                          onClick={() => props.setRedirect(createReviewHistoryLink('consumer'))}
                        >
                          View review history
                        </Button>
                      }
                    >
                      How to complete this review
                    </Header>
                  }
                >
                  <SpaceBetween size='l'>
                    <TextContent>
                      <ol>
                        <li>Select one or more resources from the table below</li>
                        <li>Choose an action or defer</li>
                        <li>Review your selections in the Action assignments table before proceeding</li>
                      </ol>
                    </TextContent>
                  </SpaceBetween>
                </Container>
                <Table
                  {...collectionProps}
                  columnDefinitions={[
                    {
                      id: 'type',
                      header: 'Type',
                      cell: (item) => getReviewInfo(item, 'type'),
                      sortingField: 'resourceType',
                    },
                    {
                      id: 'resource',
                      header: 'Name',
                      cell: (item) => getReviewInfo(item, 'resource'),
                      sortingField: 'name',
                    },
                    {
                      id: 'description',
                      header: 'Description',
                      cell: (item) => getReviewInfo(item, 'description'),
                      minWidth: 250,
                      sortingField: 'description',
                    },
                    {
                      id: 'status',
                      header: 'Status',
                      cell: (item) => getReviewInfo(item, 'status'),
                      minWidth: 250,
                      sortingField: 'reviewStatus',
                    },
                    {
                      id: 'grant',
                      header: 'Details',
                      cell: (item) => getReviewInfo(item, 'grant'),
                      isRowHeader: true,
                    },
                  ]}
                  empty={
                    <Box color='inherit' margin={{ vertical: 'xs' }} textAlign='center'>
                      <SpaceBetween size='m'>
                        <b>No pending resources to review</b>
                      </SpaceBetween>
                    </Box>
                  }
                  filter={
                    <TextFilter
                      {...filterProps}
                      filteringAriaLabel='Filter resources'
                      filteringPlaceholder='Find resources'
                      countText={`${filteredItemsCount} ${filteredItemsCount === 1 ? 'match' : 'matches'}`}
                    />
                  }
                  header={
                    <Header
                      actions={
                        <SpaceBetween direction='horizontal' size='xs'>
                          <Button variant='normal' onClick={handleDefer} disabled={selectedResources.length === 0}>
                            Defer
                          </Button>
                          <Button variant='normal' onClick={handleDelegate} disabled={selectedResources.length === 0}>
                            Defer and delegate review
                          </Button>
                          <Button variant='normal' onClick={handleRelinquish} disabled={selectedResources.length === 0}>
                            Relinquish access
                          </Button>
                          <Button variant='primary' onClick={handleKeep} disabled={selectedResources.length === 0}>
                            Keep access
                          </Button>
                        </SpaceBetween>
                      }
                      counter={`(${selectedResources.length}/${props.resourceItems.length})`}
                      description='Review each resource and assign the appropriate action using the action buttons. You can review and modify your selections before completing the process.'
                    >
                      Resources
                    </Header>
                  }
                  items={items}
                  loadingText='Loading'
                  loading={loadingReviews}
                  onSelectionChange={handleSelectionChange}
                  pagination={<Pagination {...paginationProps} />}
                  wrapLines={preferences.wrapLines}
                  preferences={
                    <CollectionPreferences
                      title={'Preferences'}
                      confirmLabel={'Confirm'}
                      cancelLabel={'Cancel'}
                      preferences={preferences}
                      onConfirm={({ detail }) => setPreferences(detail)}
                      pageSizePreference={largePageSizePreference}
                      wrapLinesPreference={defaultWrapLinesPreference}
                    />
                  }
                  selectedItems={selectedResources}
                  selectionType='multi'
                  trackBy='reviewId'
                  resizableColumns={true}
                />
              </>
            )}

            <Table
              columnDefinitions={[
                {
                  id: 'type',
                  header: 'Resource type',
                  cell: (item) => getReviewInfo(item, 'type'),
                  sortingField: 'resourceType',
                },
                {
                  id: 'resource',
                  header: 'Resource',
                  cell: (item) => getReviewInfo(item, 'resource'),
                },
                {
                  id: 'description',
                  header: 'Description',
                  cell: (item) => getReviewInfo(item, 'description'),
                  minWidth: 250,
                },
                {
                  cell: (item) => (
                    <Badge color={item['action'] == DEFER ? 'grey' : item['action'] == KEEP ? 'green' : 'red'}>
                      {item['action']}
                    </Badge>
                  ),
                  header: 'Action',
                  id: 'action',
                },
              ]}
              empty={
                <Box color='inherit' margin={{ vertical: 'xs' }} textAlign='center'>
                  <SpaceBetween size='m'>
                    <b>No resources assigned</b>
                  </SpaceBetween>
                </Box>
              }
              header={
                <Header
                  actions={
                    !props.readonly && (
                      <SpaceBetween direction='horizontal' size='xs'>
                        <Button variant='normal' onClick={handleRefresh}>
                          Reset
                        </Button>
                      </SpaceBetween>
                    )
                  }
                  description='This table shows your selected actions grouped by resource type and action.'
                >
                  Action assignments
                </Header>
              }
              items={getItemsGroupedByActionAndType(props.decidedResources)}
              loadingText='Loading'
              trackBy='reviewId'
              expandableRows={{
                getItemChildren: (item) => item.children ?? [],
                isItemExpandable: (item) => Boolean(item.children),
                expandedItems: expandedItems,
                onExpandableItemToggle: ({ detail }) =>
                  setExpandedItems((prev) => {
                    const next = new Set((prev ?? []).map((item) => item.reviewId));
                    detail.expanded ? next.add(detail.item.reviewId) : next.delete(detail.item.reviewId);
                    return [...next].map((reviewId) => ({ reviewId }));
                  }),
              }}
            />
          </>
        </SpaceBetween>
      </SpaceBetween>
    </>
  );
}

export default ReviewsAssignment;
